import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe, Shield, Server, AlertTriangle, CheckCircle, Loader2, Plus, X, RefreshCw, Trash2, Eye, Zap, Settings, Network, Lock, BarChart3, Download, Crown, ArrowUpRight } from 'lucide-react';
import AnalyticsModal from './AnalyticsModal';
import { usePermissions } from '../hooks/usePermissions';
import { LimitWarning } from './UpgradeRequired';

interface DomainConfig {
  id: number;
  domain: string;
  is_active: boolean;
  source: 'manual' | 'server_scan' | 'bulletproof';
  ssl_status: 'none' | 'pending' | 'active' | 'expired' | 'error';
  ssl_expires_at?: string;
  nginx_config_deployed: boolean;
  created_at: string;
  last_check?: string;
  linked_servers?: number[];
  cf_status?: 'none' | 'configured' | 'error';
  cf_zone_id?: string;
  ct_status?: 'clean' | 'blocked';
  cf_email?: string;
  cf_api_token?: string;
  cloudapi_type?: string;
  cloudapi_email?: string;
  cloudapi_api_key?: string;
  cloudapi_zone_id?: string;
  // BP specific fields
  bp_cloudflare_email?: string;
  bp_cloudflare_api_key?: string;
  bp_cloudflare_zone_id?: string;
}

interface ServerData {
  id: number;
  name: string;
  host: string;
  is_active: boolean;
}

const DomainsPanel: React.FC = () => {
  const navigate = useNavigate();
  const [domains, setDomains] = useState<DomainConfig[]>([]);
  const [servers, setServers] = useState<ServerData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // SSL modal
  const [showSSLModal, setShowSSLModal] = useState(false);
  const [selectedDomainForSSL, setSelectedDomainForSSL] = useState<DomainConfig | null>(null);
  const [selectedServerForSSL, setSelectedServerForSSL] = useState('');
  const [isObtainingSSL, setIsObtainingSSL] = useState(false);

  // SSL operations
  const [sslOperations, setSslOperations] = useState<{ [key: number]: boolean }>({});

  // Balance and modals
  const [balance, setBalance] = useState<number | null>(null);
  const [balanceError, setBalanceError] = useState<string>('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [addMethod, setAddMethod] = useState<'manual' | 'purchase'>('manual');
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [bearerToken, setBearerToken] = useState('');
  const [isSavingToken, setIsSavingToken] = useState(false);

  // Domain availability check
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [domainAvailability, setDomainAvailability] = useState<{ available: boolean, price: number } | null>(null);
  const [availabilityCheckTimeout, setAvailabilityCheckTimeout] = useState<NodeJS.Timeout | null>(null);
  const [lastCheckedDomain, setLastCheckedDomain] = useState<string>('');

  // Cloudflare setup
  const [setupCloudflare, setSetupCloudflare] = useState(false);
  const [cfEmail, setCfEmail] = useState('');
  const [cfToken, setCfToken] = useState('');


  // Domain modal state
  const [newDomain, setNewDomain] = useState('');
  const [isAddingDomain, setIsAddingDomain] = useState(false);
  const [domainError, setDomainError] = useState('');

  // Protection type selection
  const [selectedProtection, setSelectedProtection] = useState<'solid' | 'alpha' | 'titan'>('solid');

  // BulletProof management modals
  const [showBPManageModal, setShowBPManageModal] = useState(false);
  const [selectedBPDomain, setSelectedBPDomain] = useState<DomainConfig | null>(null);
  const [bpNewIP, setBpNewIP] = useState('');
  const [bpIPAddresses, setBpIPAddresses] = useState<string[]>([]);
  const [bpSSLMode, setBpSSLMode] = useState<'off' | 'flexible' | 'full'>('flexible');
  const [bpSecurityMode, setBpSecurityMode] = useState<'essentially_off' | 'low' | 'medium' | 'high' | 'under_attack'>('medium');
  const [isBPOperating, setIsBPOperating] = useState(false);
  const [bpDomainStatus, setBpDomainStatus] = useState<any>(null);
  const [isLoadingBPStatus, setIsLoadingBPStatus] = useState(false);

  // Subdomain management
  const [subdomains, setSubdomains] = useState<any[]>([]);
  const [isLoadingSubdomains, setIsLoadingSubdomains] = useState(false);
  const [newSubdomain, setNewSubdomain] = useState('');
  const [subdomainIP, setSubdomainIP] = useState('');
  const [isAddingSubdomain, setIsAddingSubdomain] = useState(false);

  // Custom subdomain prefix
  const [useStandardPrefix, setUseStandardPrefix] = useState(true);
  const [customPrefix, setCustomPrefix] = useState('');

  // Cloudflare settings modal
  const [showCFModal, setShowCFModal] = useState(false);
  const [selectedCFDomain, setSelectedCFDomain] = useState<DomainConfig | null>(null);
  const [cfDomainEmail, setCfDomainEmail] = useState('');
  const [cfDomainToken, setCfDomainToken] = useState('');
  const [isSavingCF, setIsSavingCF] = useState(false);
  const [cloudApiType, setCloudApiType] = useState<'cloudflare' | 'bulletproof' | ''>('');

  // Analytics modal
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [selectedAnalyticsDomain, setSelectedAnalyticsDomain] = useState<DomainConfig | null>(null);

  // Import BP Domains modal
  const [showImportBPModal, setShowImportBPModal] = useState(false);
  const [bpAccountDomains, setBpAccountDomains] = useState<any[]>([]);
  const [isLoadingBPDomains, setIsLoadingBPDomains] = useState(false);
  const [selectedBPImports, setSelectedBPImports] = useState<Set<string>>(new Set());
  const [isImportingBP, setIsImportingBP] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isCheckingCT, setIsCheckingCT] = useState(false);

  // Server selection for domain linking (servers state already exists above)
  const [selectedServerId, setSelectedServerId] = useState<number | null>(null);

  // Monitoring status
  const [monitoringStatus, setMonitoringStatus] = useState<{
    hasProxies: boolean;
    browserServiceAvailable: boolean;
    canAutoCheck: boolean;
    warnings: string[];
  } | null>(null);

  // Load monitoring status
  useEffect(() => {
    const loadMonitoringStatus = async () => {
      try {
        const response = await fetch('/api/domains/monitoring-status', {
          credentials: 'include'
        });
        const data = await response.json();
        if (data.success) {
          setMonitoringStatus(data.status);
        }
      } catch (error) {
        console.error('Failed to load monitoring status:', error);
      }
    };
    loadMonitoringStatus();
  }, []);

  // Permissions
  const { getLimit, isLimitReached, getLimitWarning, isAdmin, isEnterprise } = usePermissions();
  const domainLimit = getLimit('domains');
  const limitReached = isLimitReached('domains', domains.length);
  const limitWarning = getLimitWarning('domains', domains.length);

  useEffect(() => {
    loadDomains();
    loadServers();
    loadBalance();
    loadBulletProofSettings();
    loadCloudflareStatus();
  }, []);

  const loadCloudflareStatus = async () => {
    try {
      const response = await fetch('/api/settings/cloudflare', {
        credentials: 'include'
      });
      await response.json();
      // Token status loaded (no longer storing in state)
    } catch (error) {
      console.error('Error loading Cloudflare status:', error);
    }
  };

  const loadServers = async () => {
    try {
      const response = await fetch('/api/servers/my', {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success) {
        setServers(data.servers.filter((s: ServerData) => s.is_active));
      }
    } catch (error) {
      console.error('Error loading servers:', error);
    }
  };

  const loadBalance = async () => {
    try {
      setBalanceError('');
      const response = await fetch('/api/domains/balance', {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setBalance(data.balance);
      } else {
        setBalanceError(data.error || 'Failed to load balance');
        setBalance(null);
      }
    } catch (error) {
      console.error('Error loading balance:', error);
      setBalanceError('Network error while loading balance');
      setBalance(null);
    }
  };

  const loadBulletProofSettings = async () => {
    try {
      const response = await fetch('/api/settings/bulletproof', {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setBearerToken(data.settings.bearer_token || '');
      }
    } catch (error) {
      console.error('Error loading BulletProof settings:', error);
    }
  };

  const loadBPAccountDomains = async (showGlobalLoading = true) => {
    try {
      if (showGlobalLoading) setIsLoadingBPDomains(true);
      setError('');
      const response = await fetch('/api/domains/bp-account-domains', {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setBpAccountDomains(data.domains || []);
        // Only reset selection if we are doing a full reload/initial load, not just a status check
        if (showGlobalLoading) {
          setSelectedBPImports(new Set());
        }
      } else {
        setError(data.error || 'Failed to load BP domains');
      }
    } catch (error) {
      console.error('Error loading BP account domains:', error);
      setError('Failed to load BP account domains');
    } finally {
      if (showGlobalLoading) setIsLoadingBPDomains(false);
    }
  };

  const handleCheckCTStatus = async () => {
    if (isCheckingCT || bpAccountDomains.length === 0) return;

    setIsCheckingCT(true);

    // Create a copy of domains to track updates
    const domainsToUpdate = [...bpAccountDomains];

    try {
      for (let i = 0; i < domainsToUpdate.length; i++) {
        const domain = domainsToUpdate[i];

        // Skip already imported domains or those already determined as blocked by BP
        if (domain.alreadyImported) continue;

        // Mark current domain as checking
        domainsToUpdate[i] = { ...domain, checking: true };
        setBpAccountDomains([...domainsToUpdate]);

        try {
          const response = await fetch('/api/domains/check-ct-status', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ domain: domain.domain })
          });

          const data = await response.json();

          if (data.success) {
            // Check for CAPTCHA warning
            if (data.captchaDetected) {
              console.warn(`CAPTCHA detected for ${domain.domain}`);
              setError('CAPTCHA detected during check. Please wait a moment and try again, or add more proxies in Settings.');
            }

            const ctStatus = data.isBlocked ? 'blocked' : 'clean';
            const ctDetails = data.yandex?.details || '';

            console.log(`[CT-CHECK] ${domain.domain}: ${ctStatus}`, { details: ctDetails });

            domainsToUpdate[i] = {
              ...domain,
              checking: false,
              ct_result: ctStatus,
              ct_status: ctStatus, // Also set ct_status for consistency
              ct_details: ctDetails,
              hasProxies: data.hasProxies
            };
          } else {
            domainsToUpdate[i] = { ...domain, checking: false, ct_result: 'error' };
          }
        } catch (err) {
          console.error(`Failed to check CT for ${domain.domain}:`, err);
          domainsToUpdate[i] = { ...domain, checking: false, ct_result: 'error' };
        }

        // Update state after each domain check to show progress
        setBpAccountDomains([...domainsToUpdate]);
        console.log(`[CT-CHECK] Updated domain ${i}:`, domainsToUpdate[i]);

        // Longer delay between checks to prevent CAPTCHA (4 seconds to account for backend 3s delay)
        await new Promise(resolve => setTimeout(resolve, i === domainsToUpdate.length - 1 ? 0 : 4000));
      }
    } finally {
      setIsCheckingCT(false);
      console.log('[CT-CHECK] All checks completed. Final state:', domainsToUpdate);
    }
  };

  const handleImportBPDomains = async () => {
    if (selectedBPImports.size === 0) {
      setError('Select at least one domain to import');
      return;
    }

    try {
      setIsImportingBP(true);
      const domainsToImport = bpAccountDomains
        .filter(d => selectedBPImports.has(d.domain))
        .map(d => ({
          domain: d.domain,
          ct_status: d.ct_result || 'unknown',
          ct_details: d.ct_details || ''
        }));

      const response = await fetch('/api/domains/import-bp-domains', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          domains: domainsToImport,
          subdomain_prefix: useStandardPrefix ? 's' : customPrefix.trim(),
          ...(selectedServerId && { serverId: selectedServerId })
        })
      });

      const data = await response.json();
      if (data.success) {
        alert(data.message);
        setShowImportBPModal(false);
        await loadDomains();
      } else {
        setError(data.error || 'Failed to import domains');
      }
    } catch (error) {
      console.error('Error importing BP domains:', error);
      setError('Failed to import domains');
    } finally {
      setIsImportingBP(false);
    }
  };

  const toggleBPImportSelection = (domain: string) => {
    setSelectedBPImports(prev => {
      const newSet = new Set(prev);
      if (newSet.has(domain)) {
        newSet.delete(domain);
      } else {
        newSet.add(domain);
      }
      return newSet;
    });
  };

  const selectAllBPImports = () => {
    const notImported = bpAccountDomains.filter(d => !d.alreadyImported).map(d => d.domain);
    setSelectedBPImports(new Set(notImported));
  };

  const saveBearerToken = async () => {
    try {
      setIsSavingToken(true);
      const response = await fetch('/api/settings/bulletproof', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          bearer_token: bearerToken
        })
      });
      const data = await response.json();
      if (data.success) {
        setShowSettingsModal(false);
        alert('Bearer token saved successfully!');
        // Reload balance with new token
        await loadBalance();
      } else {
        alert('Failed to save token: ' + data.error);
      }
    } catch (error) {
      console.error('Error saving token:', error);
      alert('Failed to save token');
    } finally {
      setIsSavingToken(false);
    }
  };

  const loadDomains = async () => {
    try {
      setIsLoading(true);
      setError('');

      const response = await fetch('/api/domains', {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success) {
        setDomains(data.domains);
      } else {
        setError(data.error || 'Failed to load domains');
      }
    } catch (error) {
      console.error('Error loading domains:', error);
      setError('Failed to load domains');
    } finally {
      setIsLoading(false);
    }
  };

  const checkDomainAvailability = async (domain: string, protection?: 'solid' | 'alpha' | 'titan') => {
    if (!domain.trim() || addMethod !== 'purchase') return;

    // Use passed protection or current state
    const regType = protection || selectedProtection;
    const checkKey = `${domain.trim()}-${regType}`;

    // Prevent duplicate checks for same domain+protection combo
    if (isCheckingAvailability || checkKey === lastCheckedDomain) {
      return;
    }

    // Clear any existing timeout
    if (availabilityCheckTimeout) {
      clearTimeout(availabilityCheckTimeout);
    }

    // Debounce the check by 500ms
    const timeout = setTimeout(async () => {
      try {
        setIsCheckingAvailability(true);
        setDomainAvailability(null);
        setLastCheckedDomain(checkKey);

        const response = await fetch('/api/domains/check-availability', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            domain: domain.trim(),
            regType: regType
          })
        });

        const data = await response.json();

        if (data.success) {
          setDomainAvailability({
            available: data.available,
            price: data.price
          });
        } else {
          setDomainError(data.error || 'Failed to check domain availability');
        }
      } catch (error) {
        console.error('Error checking availability:', error);
        setDomainError('Failed to check domain availability');
      } finally {
        setIsCheckingAvailability(false);
      }
    }, 500);

    setAvailabilityCheckTimeout(timeout);
  };

  const handleAddDomain = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newDomain.trim()) {
      setDomainError('Domain is required');
      return;
    }

    // Validate domain format (supports subdomains like s.example.com)
    const domainRegex = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)*[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$/;
    if (!domainRegex.test(newDomain.trim())) {
      setDomainError('Invalid domain format');
      return;
    }

    try {
      setIsAddingDomain(true);
      setDomainError('');

      const endpoint = addMethod === 'manual' ? '/api/domains/add-manual' : '/api/domains/purchase';
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          domain: newDomain.trim(),
          subdomain_prefix: useStandardPrefix ? 's' : customPrefix.trim(),
          ...(selectedServerId && { serverId: selectedServerId }),
          ...(addMethod === 'purchase' && { regType: selectedProtection }),
          ...(addMethod === 'manual' && setupCloudflare && {
            setupCloudflare: true,
            ...(cfToken.trim() && { cfApiToken: cfToken.trim() }),
            ...(cfEmail.trim() && { cfEmail: cfEmail.trim() })
          })
        })
      });

      const data = await response.json();

      if (data.success) {
        await loadDomains();
        await loadBalance(); // Reload balance after purchase
        setShowAddModal(false);
        setNewDomain('');
        setDomainAvailability(null);
        setDomainError('');
        setSetupCloudflare(false);
        setCfToken('');
        setLastCheckedDomain(''); // Reset to allow re-checking
        if (availabilityCheckTimeout) clearTimeout(availabilityCheckTimeout);

        // Show success message for manual domains
        if (addMethod === 'manual' && data.message) {
          setError(data.message);
          setTimeout(() => setError(''), 5000); // Clear message after 5 seconds
        }
      } else {
        setDomainError(data.error || `Failed to ${addMethod === 'manual' ? 'add' : 'purchase'} domain`);
      }
    } catch (error) {
      console.error('Error adding domain:', error);
      setDomainError(`Failed to ${addMethod === 'manual' ? 'add' : 'purchase'} domain`);
    } finally {
      setIsAddingDomain(false);
    }
  };



  const handleObtainSSL = async () => {
    if (!selectedDomainForSSL || !selectedServerForSSL) return;

    try {
      setIsObtainingSSL(true);
      setSslOperations({ ...sslOperations, [selectedDomainForSSL.id]: true });

      const response = await fetch(`/api/domains/${selectedDomainForSSL.id}/obtain-ssl`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          serverId: parseInt(selectedServerForSSL)
        })
      });

      const data = await response.json();

      if (data.success) {
        await loadDomains();
        setShowSSLModal(false);
        setSelectedDomainForSSL(null);
        setSelectedServerForSSL('');
      } else {
        const errorMsg = data.error || 'Failed to obtain SSL certificate';
        // Check if it's a rate limit error
        if (errorMsg.includes('rate limit') || errorMsg.includes('too many certificates')) {
          setError(`${errorMsg}\n\nTip: Wait for the rate limit to reset, or use Let's Encrypt staging for testing.`);
        } else {
          setError(errorMsg);
        }
      }
    } catch (error) {
      console.error('Error obtaining SSL:', error);
      setError('Failed to obtain SSL certificate');
    } finally {
      setIsObtainingSSL(false);
      if (selectedDomainForSSL) {
        setSslOperations({ ...sslOperations, [selectedDomainForSSL.id]: false });
      }
    }
  };



  const handleDeleteDomain = async (domainId: number) => {
    if (!confirm('Are you sure you want to delete this domain?')) {
      return;
    }

    try {
      const response = await fetch(`/api/settings/domains/${domainId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success) {
        await loadDomains();
      } else {
        setError(data.error || 'Failed to delete domain');
      }
    } catch (error) {
      console.error('Error deleting domain:', error);
      setError('Failed to delete domain');
    }
  };

  const handleOpenBPManage = async (domain: DomainConfig) => {
    setSelectedBPDomain(domain);
    setBpNewIP('');
    setBpIPAddresses([]);
    setBpSSLMode('flexible');
    setBpSecurityMode('medium');
    setBpDomainStatus(null);
    setSubdomains([]);
    setNewSubdomain('');
    setSubdomainIP('');
    setShowBPManageModal(true);

    // Load current status and subdomains
    await loadBPDomainStatus(domain.id);
    await loadSubdomains(domain.id);
  };

  const loadBPDomainStatus = async (domainId: number) => {
    try {
      setIsLoadingBPStatus(true);
      const response = await fetch(`/api/domains/${domainId}/bp-check-status`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success) {
        setBpDomainStatus(data.domain);
        if (data.domain.ssl_mode) setBpSSLMode(data.domain.ssl_mode);
        if (data.domain.security_mode) setBpSecurityMode(data.domain.security_mode);
        if (data.domain.ip) setBpNewIP(data.domain.ip);
      } else {
        setError(data.error || 'Failed to load domain status');
      }
    } catch (error) {
      console.error('Error loading BP status:', error);
      setError('Failed to load domain status');
    } finally {
      setIsLoadingBPStatus(false);
    }
  };

  const loadSubdomains = async (domainId: number) => {
    try {
      setIsLoadingSubdomains(true);
      const response = await fetch(`/api/domains/${domainId}/subdomains`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success) {
        setSubdomains(data.records || []);
      }
    } catch (error) {
      console.error('Error loading subdomains:', error);
    } finally {
      setIsLoadingSubdomains(false);
    }
  };

  const handleAddSubdomain = async () => {
    if (!selectedBPDomain || !newSubdomain.trim() || !subdomainIP.trim()) return;

    try {
      setIsAddingSubdomain(true);

      // Extract subdomain prefix from full domain
      const fullDomain = newSubdomain.trim();
      const mainDomain = selectedBPDomain.domain;
      let subdomainPrefix = fullDomain;

      // If user entered full domain like "api.example.com", extract "api"
      if (fullDomain.endsWith(`.${mainDomain}`)) {
        subdomainPrefix = fullDomain.replace(`.${mainDomain}`, '');
      } else if (fullDomain.includes('.')) {
        // If contains dot but doesn't end with main domain, use as is
        subdomainPrefix = fullDomain.split('.')[0];
      }

      const response = await fetch(`/api/domains/${selectedBPDomain.id}/add-subdomain`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          subdomain: subdomainPrefix,
          ipAddress: subdomainIP.trim()
        })
      });

      const data = await response.json();

      if (data.success) {
        alert('Subdomain added successfully!');
        setNewSubdomain('');
        setSubdomainIP('');
        await loadSubdomains(selectedBPDomain.id);
      } else {
        setError(data.error || 'Failed to add subdomain');
      }
    } catch (error) {
      console.error('Error adding subdomain:', error);
      setError('Failed to add subdomain');
    } finally {
      setIsAddingSubdomain(false);
    }
  };

  const handleDeleteSubdomain = async (recordId: string) => {
    if (!selectedBPDomain) return;

    try {
      const response = await fetch(`/api/domains/${selectedBPDomain.id}/subdomain/${recordId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success) {
        alert('Subdomain deleted successfully!');
        await loadSubdomains(selectedBPDomain.id);
      } else {
        setError(data.error || 'Failed to delete subdomain');
      }
    } catch (error) {
      console.error('Error deleting subdomain:', error);
      setError('Failed to delete subdomain');
    }
  };

  const handleOpenCFSettings = (domain: DomainConfig) => {
    setSelectedCFDomain(domain);
    setCfDomainEmail(domain.cloudapi_email || domain.cf_email || '');
    setCfDomainToken(domain.cloudapi_api_key || domain.cf_api_token || '');
    setCloudApiType(domain.cloudapi_type as 'cloudflare' | 'bulletproof' || '');
    setShowCFModal(true);
  };

  const handleOpenAnalytics = (domain: DomainConfig) => {
    setSelectedAnalyticsDomain(domain);
    setShowAnalyticsModal(true);
  };

  const handleSaveCFSettings = async () => {
    if (!selectedCFDomain) return;

    try {
      setIsSavingCF(true);
      const response = await fetch(`/api/domains/${selectedCFDomain.id}/cloudapi-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          cloudapi_type: cloudApiType,
          cloudapi_email: cfDomainEmail.trim(),
          cloudapi_api_key: cfDomainToken.trim()
        })
      });

      const data = await response.json();

      if (data.success) {
        alert('CloudAPI settings saved successfully!');
        setShowCFModal(false);
        await loadDomains();
      } else {
        setError(data.error || 'Failed to save CloudAPI settings');
      }
    } catch (error) {
      console.error('Error saving CloudAPI settings:', error);
      setError('Failed to save CloudAPI settings');
    } finally {
      setIsSavingCF(false);
    }
  };

  const handleBPChangeIP = async () => {
    if (!selectedBPDomain || bpIPAddresses.length === 0) return;

    try {
      setIsBPOperating(true);
      const response = await fetch(`/api/domains/${selectedBPDomain.id}/bp-change-ip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ipAddresses: bpIPAddresses })
      });

      const data = await response.json();

      if (data.success) {
        alert(`IP addresses changed successfully! (${bpIPAddresses.length} IPs)`);
        setBpIPAddresses([]);
        await loadBPDomainStatus(selectedBPDomain.id);
      } else {
        setError(data.error || 'Failed to change IP');
      }
    } catch (error) {
      console.error('Error changing IP:', error);
      setError('Failed to change IP');
    } finally {
      setIsBPOperating(false);
    }
  };

  const handleBPChangeSSL = async () => {
    if (!selectedBPDomain) return;

    try {
      setIsBPOperating(true);
      const response = await fetch(`/api/domains/${selectedBPDomain.id}/bp-change-ssl`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ sslMode: bpSSLMode })
      });

      const data = await response.json();

      if (data.success) {
        alert('SSL mode changed successfully!');
        await loadBPDomainStatus(selectedBPDomain.id);
      } else {
        setError(data.error || 'Failed to change SSL mode');
      }
    } catch (error) {
      console.error('Error changing SSL mode:', error);
      setError('Failed to change SSL mode');
    } finally {
      setIsBPOperating(false);
    }
  };

  const handleBPChangeSecurity = async () => {
    if (!selectedBPDomain) return;

    try {
      setIsBPOperating(true);
      const response = await fetch(`/api/domains/${selectedBPDomain.id}/bp-change-security`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ securityMode: bpSecurityMode })
      });

      const data = await response.json();

      if (data.success) {
        alert('Security mode changed successfully!');
        await loadBPDomainStatus(selectedBPDomain.id);
      } else {
        setError(data.error || 'Failed to change security mode');
      }
    } catch (error) {
      console.error('Error changing security mode:', error);
      setError('Failed to change security mode');
    } finally {
      setIsBPOperating(false);
    }
  };

  const getSSLStatusBadge = (status: string, expiresAt?: string) => {
    const statusConfig = {
      none: { color: 'text-text-muted bg-table-row-dark', label: 'No SSL', icon: Shield },
      pending: { color: 'text-accent-blue bg-accent-blue/10', label: 'Pending', icon: Loader2 },
      active: { color: 'text-accent-green bg-accent-green/10', label: 'Active', icon: CheckCircle },
      expired: { color: 'text-accent-red bg-accent-red/10', label: 'Expired', icon: AlertTriangle },
      error: { color: 'text-accent-red bg-accent-red/10', label: 'Error', icon: AlertTriangle }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.none;
    const IconComponent = config.icon;

    return (
      <div className="space-y-1">
        <div className="flex items-center space-x-2">
          <IconComponent className={`w-4 h-4 ${config.color.split(' ')[0]}`} />
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
            {config.label}
          </span>
        </div>
        {expiresAt && status === 'active' && (
          <div className="text-xs text-text-muted pl-6">
            Expires: {new Date(expiresAt).toLocaleDateString()}
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="w-full h-full p-8 animate-scale-in">
        <div className="flex items-center justify-center h-64">
          <div className="text-center space-y-4">
            <Loader2 className="w-12 h-12 text-accent-blue animate-spin mx-auto" />
            <p className="text-text-muted">Loading domains...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full p-8 animate-scale-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-12">
        <div>
          <h1 className="text-4xl font-bold text-text-primary mb-4">Domains</h1>
          {balance !== null ? (
            <div className="flex items-center space-x-2">
              <span className="text-text-muted">BulletProof Balance:</span>
              <span className="text-accent-green font-bold">${typeof balance === 'number' ? balance.toFixed(2) : '0.00'}</span>
            </div>
          ) : balanceError && (
            <div className="flex items-center space-x-2">
              <AlertTriangle className="w-5 h-5 text-accent-red" />
              <span className="text-accent-red text-sm">{balanceError}</span>
              <button
                onClick={() => setShowSettingsModal(true)}
                className="text-accent-blue hover:text-accent-blue-hover text-sm underline"
              >
                Check Settings
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setShowSettingsModal(true)}
            className="btn-secondary flex items-center space-x-2"
          >
            <Settings className="w-5 h-5" />
            <span>BulletProof Settings</span>
          </button>
          {domains.length > 0 && (
            limitReached ? (
              <button
                onClick={() => setShowUpgradeModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white/50 rounded-lg text-sm font-medium cursor-not-allowed"
                disabled
              >
                <Lock className="w-4 h-4" />
                Domain Limit Reached
              </button>
            ) : (
              <button
                onClick={() => setShowAddModal(true)}
                className="btn-primary flex items-center space-x-2"
              >
                <Plus className="w-5 h-5" />
                <span>Add Domain</span>
              </button>
            )
          )}
        </div>
      </div>

      {/* Monitoring Warnings */}
      {monitoringStatus && !monitoringStatus.hasProxies && !isAdmin && isEnterprise && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-500" />
            <span className="text-yellow-500 font-medium">
              No proxies configured
            </span>
          </div>
          <p className="text-sm text-gray-400 mt-2">
            Automatic CT checks are disabled. Add proxies in Notifications to enable domain monitoring.
          </p>
          <button
            onClick={() => navigate('/notifications?tab=browser-check')}
            className="mt-2 text-sm text-accent-blue hover:underline"
          >
            Go to Notifications →
          </button>
        </div>
      )}

      {/* Limit Warning */}
      {limitWarning !== 'none' && (
        <div className="mb-6">
          <LimitWarning
            resource="domains"
            current={domains.length}
            limit={domainLimit}
            severity={limitWarning}
          />
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-accent-red/10 border border-accent-red/20 rounded-lg">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5 text-accent-red" />
            <p className="text-accent-red font-medium">{error}</p>
          </div>
        </div>
      )}



      {/* Domains Grid */}
      <div className="bg-table-bg rounded-xl p-6">
        {domains.length === 0 ? (
          <div className="text-center py-16">
            <Globe className="w-16 h-16 text-text-muted mx-auto mb-6" />
            <h3 className="text-xl font-semibold text-text-primary mb-2">No domains configured</h3>
            <p className="text-text-muted mb-6">Add your first domain to start managing SSL certificates and nginx configs</p>
            {limitReached ? (
              <button
                onClick={() => setShowUpgradeModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white/50 rounded-lg text-sm font-medium cursor-not-allowed mx-auto"
                disabled
              >
                <Lock className="w-4 h-4" />
                Domain Limit Reached
              </button>
            ) : (
              <button
                onClick={() => setShowAddModal(true)}
                className="btn-primary flex items-center space-x-2 mx-auto"
              >
                <Plus className="w-5 h-5" />
                <span>Add Domain</span>
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto pb-4">
            <div className="flex space-x-6 min-w-min">
              {domains.map((domain) => (
                <div key={domain.id} className="card p-6 flex flex-col flex-shrink-0 w-96">
                  {/* Domain Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-text-primary mb-1">{domain.domain}</h3>
                      <p className="text-xs text-text-muted">
                        {new Date(domain.created_at).toLocaleDateString('en-GB')}
                      </p>
                    </div>
                    <div className="flex flex-col items-end space-y-1.5">
                      <div className="flex items-center space-x-1.5">
                        <div className={`w-2 h-2 rounded-full ${domain.is_active ? 'bg-accent-green' : 'bg-text-muted'
                          }`}></div>
                        <span className={`text-xs font-medium ${domain.is_active ? 'text-accent-green' : 'text-text-muted'
                          }`}>
                          {domain.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      {domain.source === 'server_scan' && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-accent-blue/10 text-accent-blue rounded-full">
                          Imported
                        </span>
                      )}
                      {domain.source === 'bulletproof' && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-accent-purple/10 text-accent-purple rounded-full">
                          BulletProof
                        </span>
                      )}
                    </div>
                  </div>

                  {/* SSL Status */}
                  <div className="space-y-2 mb-4">
                    <h4 className="text-sm font-semibold text-text-primary">SSL Certificate</h4>
                    {getSSLStatusBadge(domain.ssl_status, domain.ssl_expires_at)}
                  </div>



                  {/* CT Status */}
                  <div className="space-y-2 mb-4">
                    <h4 className="text-sm font-semibold text-text-primary">CT Status</h4>
                    <div className="flex items-center space-x-2">
                      {domain.ct_status === 'blocked' ? (
                        <>
                          <AlertTriangle className="w-4 h-4 text-accent-red" />
                          <span className="px-2 py-1 text-xs font-medium bg-accent-red/10 text-accent-red rounded-full">
                            Blocked
                          </span>
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 text-accent-green" />
                          <span className="px-2 py-1 text-xs font-medium bg-accent-green/10 text-accent-green rounded-full">
                            Clean
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Cloudflare DNS Status */}
                  {domain.cf_status && domain.cf_status !== 'none' && (
                    <div className="space-y-2 mb-4">
                      <h4 className="text-sm font-semibold text-text-primary">Cloudflare DNS</h4>
                      <div className="flex items-center space-x-2">
                        {domain.cf_status === 'configured' ? (
                          <>
                            <CheckCircle className="w-4 h-4 text-accent-purple" />
                            <span className="text-sm text-accent-purple font-medium">Configured</span>
                          </>
                        ) : domain.cf_status === 'error' ? (
                          <>
                            <AlertTriangle className="w-4 h-4 text-accent-red" />
                            <span className="text-sm text-accent-red font-medium">Error</span>
                          </>
                        ) : null}
                      </div>
                      {domain.cf_zone_id && (
                        <div className="text-xs text-text-muted truncate" title={domain.cf_zone_id}>
                          Zone ID: {domain.cf_zone_id.substring(0, 8)}...
                        </div>
                      )}
                    </div>
                  )}

                  {/* BP API Configuration Status - for bulletproof domains */}
                  {domain.source === 'bulletproof' && (
                    <div className="space-y-2 mb-4">
                      <h4 className="text-sm font-semibold text-text-primary">BP Cloud API</h4>
                      <div className="flex items-center space-x-2">
                        {(domain.bp_cloudflare_api_key || domain.cloudapi_api_key) ? (
                          <>
                            <CheckCircle className="w-4 h-4 text-accent-green" />
                            <span className="px-2 py-1 text-xs font-medium bg-accent-green/10 text-accent-green rounded-full">
                              API Configured
                            </span>
                          </>
                        ) : (
                          <>
                            <AlertTriangle className="w-4 h-4 text-accent-yellow" />
                            <span className="px-2 py-1 text-xs font-medium bg-accent-yellow/10 text-accent-yellow rounded-full">
                              API Not Set
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center justify-between pt-4 mt-auto border-t border-border-default">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleOpenAnalytics(domain)}
                        className="p-2 text-accent-green hover:bg-accent-green/10 rounded-lg transition-colors"
                        title="View Analytics"
                      >
                        <BarChart3 className="w-4 h-4" />
                      </button>

                      {domain.source === 'bulletproof' && (
                        <button
                          onClick={() => handleOpenBPManage(domain)}
                          className="p-2 text-accent-purple hover:bg-accent-purple/10 rounded-lg transition-colors"
                          title="Manage BulletProof Domain"
                        >
                          <Settings className="w-4 h-4" />
                        </button>
                      )}

                      <button
                        onClick={() => handleOpenCFSettings(domain)}
                        className="p-2 text-accent-blue hover:bg-accent-blue/10 rounded-lg transition-colors"
                        title="Cloudflare Settings"
                      >
                        <Network className="w-4 h-4" />
                      </button>

                      <button
                        onClick={() => loadDomains()}
                        className="p-2 text-text-muted hover:bg-table-row-hover rounded-lg transition-colors"
                        title="Refresh Status"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </button>
                    </div>

                    <button
                      onClick={() => handleDeleteDomain(domain.id)}
                      className="p-2 text-accent-red hover:bg-accent-red/10 rounded-lg transition-colors"
                      title="Delete Domain"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-12">
        <button
          onClick={() => {
            setAddMethod('manual');
            setShowAddModal(true);
          }}
          className="card p-6 text-center hover:bg-card-bg-secondary transition-colors group"
        >
          <div className="w-12 h-12 bg-accent-blue/10 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-accent-blue/20 transition-colors">
            <Server className="w-6 h-6 text-accent-blue" />
          </div>
          <h3 className="font-semibold text-text-primary mb-2">My Domains</h3>
          <p className="text-sm text-text-muted">Add existing domains manually</p>
        </button>

        <button
          onClick={() => {
            setAddMethod('purchase');
            setShowAddModal(true);
          }}
          className="card p-6 text-center hover:bg-card-bg-secondary transition-colors group"
        >
          <div className="w-12 h-12 bg-accent-green/10 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-accent-green/20 transition-colors">
            <Globe className="w-6 h-6 text-accent-green" />
          </div>
          <h3 className="font-semibold text-text-primary mb-2">Purchase Domains</h3>
          <p className="text-sm text-text-muted">Buy domains through BulletProof</p>
        </button>

        <button
          onClick={() => {
            setShowImportBPModal(true);
            loadBPAccountDomains();
          }}
          className="card p-6 text-center hover:bg-card-bg-secondary transition-colors group"
        >
          <div className="w-12 h-12 bg-accent-purple/10 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-accent-purple/20 transition-colors">
            <Download className="w-6 h-6 text-accent-purple" />
          </div>
          <h3 className="font-semibold text-text-primary mb-2">Import BP Domains</h3>
          <p className="text-sm text-text-muted">Import domains from BulletProof account</p>
        </button>
      </div>

      {/* Add Domain Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`bg-card-bg border border-border-default rounded-xl p-6 w-full mx-4 ${addMethod === 'purchase' ? 'max-w-lg' : 'max-w-md'}`}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                {addMethod === 'manual' ? (
                  <div className="w-10 h-10 rounded-xl bg-accent-blue/20 flex items-center justify-center">
                    <Globe className="w-5 h-5 text-accent-blue" />
                  </div>
                ) : (
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent-green/20 to-accent-green/10 flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="w-5 h-5 text-accent-green" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="9" cy="21" r="1" />
                      <circle cx="20" cy="21" r="1" />
                      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                    </svg>
                  </div>
                )}
                <div>
                  <h3 className="text-lg font-bold text-text-primary">
                    {addMethod === 'manual' ? 'Add Existing Domain' : 'Purchase Domain'}
                  </h3>
                  <p className="text-xs text-text-muted">
                    {addMethod === 'manual' ? 'Add your existing domain' : 'Buy domain through BulletProof'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewDomain('');
                  setDomainAvailability(null);
                  setDomainError('');
                  setSetupCloudflare(false);
                  setCfEmail('');
                  setCfToken('');
                }}
                className="p-2 hover:bg-table-row-hover rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-text-muted" />
              </button>
            </div>

            {domainError && (
              <div className="mb-4 p-3 bg-accent-red/10 border border-accent-red/20 rounded-lg">
                <p className="text-accent-red text-sm">{domainError}</p>
              </div>
            )}

            <form onSubmit={handleAddDomain} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-text-primary mb-2">
                  Domain Name
                </label>
                <div className="relative">
                  <input
                    type="text"
                    className="w-full px-4 py-3 bg-table-bg border border-border-default rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent transition-all"
                    value={newDomain}
                    onChange={(e) => {
                      setNewDomain(e.target.value);
                      setDomainAvailability(null);
                      setDomainError('');
                    }}
                    onBlur={() => checkDomainAvailability(newDomain)}
                    placeholder="example.com"
                    required
                  />
                  {isCheckingAvailability && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <Loader2 className="w-5 h-5 text-accent-blue animate-spin" />
                    </div>
                  )}
                </div>
                <p className="text-xs text-text-muted mt-2">
                  {addMethod === 'manual'
                    ? 'Enter your existing domain name (e.g., example.com)'
                    : 'Enter domain name to purchase (e.g., example.com)'
                  }
                </p>
              </div>

              {/* Info for manual domains */}
              {addMethod === 'manual' && (
                <div className="p-3 bg-white/5 border border-white/10 rounded-lg">
                  <p className="text-text-muted text-sm">
                    Domain will be added to your list.
                  </p>
                </div>
              )}
              {addMethod === 'purchase' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-semibold text-text-primary">
                      Protection Level
                    </label>
                    {isCheckingAvailability && (
                      <div className="flex items-center space-x-2 text-accent-blue text-xs">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        <span>Checking price...</span>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    {/* Solid Protection */}
                    <button
                      type="button"
                      className={`relative p-4 rounded-xl border-2 transition-all duration-200 ${selectedProtection === 'solid'
                        ? 'border-accent-green bg-gradient-to-br from-accent-green/20 to-accent-green/5 shadow-lg shadow-accent-green/20'
                        : 'border-border-default hover:border-accent-green/50 bg-table-bg'
                        }`}
                      onClick={() => {
                        setSelectedProtection('solid');
                        if (newDomain.trim()) {
                          checkDomainAvailability(newDomain, 'solid');
                        }
                      }}
                    >
                      <div className="flex flex-col items-center text-center space-y-2">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${selectedProtection === 'solid' ? 'bg-accent-green/20' : 'bg-table-row-dark'
                          }`}>
                          <Shield className={`w-5 h-5 ${selectedProtection === 'solid' ? 'text-accent-green' : 'text-text-muted'}`} />
                        </div>
                        <h4 className={`font-semibold text-sm ${selectedProtection === 'solid' ? 'text-accent-green' : 'text-text-primary'}`}>
                          Solid
                        </h4>
                        <p className="text-xs text-text-muted">Basic</p>
                      </div>
                      {selectedProtection === 'solid' && (
                        <div className="absolute top-2 right-2 w-3 h-3 rounded-full bg-accent-green animate-pulse" />
                      )}
                    </button>

                    {/* Alpha Protection */}
                    <button
                      type="button"
                      className={`relative p-4 rounded-xl border-2 transition-all duration-200 ${selectedProtection === 'alpha'
                        ? 'border-accent-blue bg-gradient-to-br from-accent-blue/20 to-accent-blue/5 shadow-lg shadow-accent-blue/20'
                        : 'border-border-default hover:border-accent-blue/50 bg-table-bg'
                        }`}
                      onClick={() => {
                        setSelectedProtection('alpha');
                        if (newDomain.trim()) {
                          checkDomainAvailability(newDomain, 'alpha');
                        }
                      }}
                    >
                      <div className="flex flex-col items-center text-center space-y-2">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${selectedProtection === 'alpha' ? 'bg-accent-blue/20' : 'bg-table-row-dark'
                          }`}>
                          <Zap className={`w-5 h-5 ${selectedProtection === 'alpha' ? 'text-accent-blue' : 'text-text-muted'}`} />
                        </div>
                        <h4 className={`font-semibold text-sm ${selectedProtection === 'alpha' ? 'text-accent-blue' : 'text-text-primary'}`}>
                          Alpha
                        </h4>
                        <p className="text-xs text-text-muted">Enhanced</p>
                      </div>
                      {selectedProtection === 'alpha' && (
                        <div className="absolute top-2 right-2 w-3 h-3 rounded-full bg-accent-blue animate-pulse" />
                      )}
                    </button>

                    {/* Titan Protection */}
                    <button
                      type="button"
                      className={`relative p-4 rounded-xl border-2 transition-all duration-200 ${selectedProtection === 'titan'
                        ? 'border-accent-purple bg-gradient-to-br from-accent-purple/20 to-accent-purple/5 shadow-lg shadow-accent-purple/20'
                        : 'border-border-default hover:border-accent-purple/50 bg-table-bg'
                        }`}
                      onClick={() => {
                        setSelectedProtection('titan');
                        if (newDomain.trim()) {
                          checkDomainAvailability(newDomain, 'titan');
                        }
                      }}
                    >
                      <div className="flex flex-col items-center text-center space-y-2">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${selectedProtection === 'titan' ? 'bg-accent-purple/20' : 'bg-table-row-dark'
                          }`}>
                          <Shield className={`w-5 h-5 ${selectedProtection === 'titan' ? 'text-accent-purple' : 'text-text-muted'}`} />
                        </div>
                        <h4 className={`font-semibold text-sm ${selectedProtection === 'titan' ? 'text-accent-purple' : 'text-text-primary'}`}>
                          Titan
                        </h4>
                        <p className="text-xs text-text-muted">Maximum</p>
                      </div>
                      {selectedProtection === 'titan' && (
                        <div className="absolute top-2 right-2 w-3 h-3 rounded-full bg-accent-purple animate-pulse" />
                      )}
                    </button>
                  </div>

                  {/* Pricing Card */}
                  <div className={`rounded-xl p-4 transition-all duration-300 ${domainAvailability?.available
                    ? 'bg-gradient-to-r from-accent-green/10 to-accent-green/5 border border-accent-green/30'
                    : domainAvailability && !domainAvailability.available
                      ? 'bg-gradient-to-r from-accent-red/10 to-accent-red/5 border border-accent-red/30'
                      : 'bg-table-row-dark border border-border-default'
                    }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        {domainAvailability?.available ? (
                          <CheckCircle className="w-6 h-6 text-accent-green" />
                        ) : domainAvailability && !domainAvailability.available ? (
                          <AlertTriangle className="w-6 h-6 text-accent-red" />
                        ) : (
                          <Globe className="w-6 h-6 text-text-muted" />
                        )}
                        <div>
                          <p className="font-medium text-text-primary">
                            {newDomain.trim() || 'Enter domain name'}
                          </p>
                          <p className={`text-xs ${domainAvailability?.available
                            ? 'text-accent-green'
                            : domainAvailability && !domainAvailability.available
                              ? 'text-accent-red'
                              : 'text-text-muted'
                            }`}>
                            {domainAvailability?.available
                              ? 'Domain is available!'
                              : domainAvailability && !domainAvailability.available
                                ? 'Domain not available'
                                : 'Check availability'}
                          </p>
                        </div>
                      </div>
                      {domainAvailability?.available && (
                        <div className="text-right">
                          <p className="text-2xl font-bold text-accent-green">
                            ${domainAvailability.price.toFixed(2)}
                          </p>
                          <p className="text-xs text-text-muted capitalize">{selectedProtection} plan</p>
                        </div>
                      )}
                    </div>

                    {/* Balance Info */}
                    {balance !== null && domainAvailability?.available && (
                      <div className="mt-3 pt-3 border-t border-border-default/30">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-text-muted">Your Balance</span>
                          <span className={`font-semibold ${balance >= domainAvailability.price ? 'text-accent-green' : 'text-accent-red'}`}>
                            ${balance.toFixed(2)}
                          </span>
                        </div>
                        {balance < domainAvailability.price && (
                          <p className="text-xs text-accent-red mt-1">
                            Need ${(domainAvailability.price - balance).toFixed(2)} more
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Server Selection - for purchase and manual */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-text-primary">
                  Link to Server (Optional)
                </label>
                <select
                  className="w-full px-4 py-3 bg-table-bg border border-border-default rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent transition-all"
                  value={selectedServerId || ''}
                  onChange={(e) => setSelectedServerId(e.target.value ? parseInt(e.target.value) : null)}
                >
                  <option value="">No server (add later)</option>
                  {servers.map((server) => (
                    <option key={server.id} value={server.id}>
                      {server.name} ({server.host})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-text-muted">
                  Select a server to automatically link this domain
                </p>
              </div>

              {/* Subdomain Prefix Toggle - for both manual and purchase */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold text-text-primary">
                    Subdomain Prefix
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <span className={`text-sm ${useStandardPrefix ? 'text-accent-green' : 'text-text-muted'}`}>
                      Use s.
                    </span>
                    <div
                      className={`relative w-12 h-6 rounded-full transition-colors ${useStandardPrefix ? 'bg-accent-green' : 'bg-table-row-dark'}`}
                      onClick={() => setUseStandardPrefix(!useStandardPrefix)}
                    >
                      <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${useStandardPrefix ? 'left-7' : 'left-1'}`} />
                    </div>
                  </label>
                </div>

                {!useStandardPrefix && (
                  <div>
                    <input
                      type="text"
                      className="w-full px-4 py-3 bg-table-bg border border-border-default rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent transition-all"
                      value={customPrefix}
                      onChange={(e) => setCustomPrefix(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                      placeholder="custom"
                    />
                    <p className="text-xs text-text-muted mt-1">
                      Final URL: <span className="text-accent-blue">{customPrefix || 'custom'}.{newDomain || 'example.com'}</span>
                    </p>
                  </div>
                )}

                {useStandardPrefix && (
                  <p className="text-xs text-text-muted">
                    Final URL: <span className="text-accent-green">s.{newDomain || 'example.com'}</span>
                  </p>
                )}
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setNewDomain('');
                    setDomainAvailability(null);
                    setDomainError('');
                  }}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    isAddingDomain ||
                    (addMethod === 'purchase' && (!domainAvailability?.available ||
                      (balance !== null && domainAvailability && balance < domainAvailability.price)))
                  }
                  className="btn-primary flex-1 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isAddingDomain ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>{addMethod === 'manual' ? 'Adding...' : 'Purchasing...'}</span>
                    </>
                  ) : (
                    <>
                      {addMethod === 'manual' ? <Plus className="w-4 h-4" /> : <Globe className="w-4 h-4" />}
                      <span>{addMethod === 'manual' ? 'Add Domain' : 'Purchase Domain'}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Obtain SSL Modal */}
      {showSSLModal && selectedDomainForSSL && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card-bg border border-border-default rounded-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-text-primary">Obtain SSL Certificate</h3>
              <button
                onClick={() => {
                  setShowSSLModal(false);
                  setSelectedDomainForSSL(null);
                  setSelectedServerForSSL('');
                }}
                className="p-2 hover:bg-table-row-hover rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-text-muted" />
              </button>
            </div>

            <div className="mb-4 p-4 bg-accent-green/10 border border-accent-green/20 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <div className="flex items-center justify-center">
                  <Globe className="w-5 h-5 text-accent-green" />
                </div>
                <p className="text-accent-green font-semibold">{selectedDomainForSSL.domain}</p>
              </div>
              <div className="flex items-center space-x-2 mt-2">
                <Shield className="w-4 h-4 text-accent-green" />
                <p className="text-accent-green text-sm">
                  SSL certificate will be obtained only on the selected server
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-text-primary mb-2">
                  Select Server
                </label>
                {servers.filter(s => selectedDomainForSSL.linked_servers?.includes(s.id)).length === 0 ? (
                  <div className="p-3 bg-accent-red/10 border border-accent-red/20 rounded-lg">
                    <p className="text-accent-red text-sm">
                      No linked servers found. Please deploy nginx configuration first.
                    </p>
                  </div>
                ) : (
                  <select
                    className="w-full px-4 py-3 bg-table-bg border border-border-default rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-green focus:border-transparent transition-all"
                    value={selectedServerForSSL}
                    onChange={(e) => setSelectedServerForSSL(e.target.value)}
                    required
                  >
                    <option value="">Choose a server...</option>
                    {servers.filter(s => selectedDomainForSSL.linked_servers?.includes(s.id)).map((server) => (
                      <option key={server.id} value={server.id}>
                        {server.name} ({server.host})
                      </option>
                    ))}
                  </select>
                )}
                <p className="text-xs text-text-muted mt-2">
                  Select the specific server where you want to obtain SSL certificate
                </p>
              </div>

              <div className="p-3 bg-accent-blue/10 border border-accent-blue/20 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Lock className="w-4 h-4 text-accent-blue" />
                  <p className="text-accent-blue text-sm font-medium">
                    Single Server SSL
                  </p>
                </div>
                <p className="text-accent-blue text-xs mt-1">
                  SSL will be obtained ONLY on the selected server. This prevents certificate conflicts when multiple servers are configured.
                </p>
              </div>
            </div>

            <div className="flex space-x-3 pt-6">
              <button
                type="button"
                onClick={() => {
                  setShowSSLModal(false);
                  setSelectedDomainForSSL(null);
                  setSelectedServerForSSL('');
                }}
                className="btn-secondary flex-1"
                disabled={isObtainingSSL}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleObtainSSL}
                disabled={!selectedServerForSSL || isObtainingSSL}
                className="btn-primary flex-1 flex items-center justify-center space-x-2"
              >
                {isObtainingSSL ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Obtaining SSL...</span>
                  </>
                ) : (
                  <>
                    <Shield className="w-4 h-4" />
                    <span>Obtain SSL</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}



      {/* BulletProof Settings Modal */}
      {showSettingsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card-bg border border-border-default rounded-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-text-primary">BulletProof Settings</h3>
              <button
                onClick={() => setShowSettingsModal(false)}
                className="p-2 hover:bg-table-row-hover rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-text-muted" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-text-primary mb-2">
                  Bearer Token
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-table-bg border border-border-default rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent transition-all"
                  value={bearerToken}
                  onChange={(e) => setBearerToken(e.target.value)}
                  placeholder="Enter your BulletProof API token"
                />
                <p className="text-xs text-text-muted mt-2">
                  Enter your BulletProof API token to enable domain purchasing
                </p>
              </div>

              <div className="p-3 bg-accent-blue/10 border border-accent-blue/20 rounded-lg">
                <h4 className="font-semibold text-accent-blue mb-2">How to get Bearer Token:</h4>
                <ol className="text-sm text-accent-blue space-y-1">
                  <li>1. Login to your BulletProof account</li>
                  <li>2. Go to Dashboard → API Settings</li>
                  <li>3. Generate or copy your Bearer token</li>
                  <li>4. Paste it here to enable domain purchasing</li>
                </ol>
              </div>
            </div>

            <div className="flex space-x-3 pt-6">
              <button
                type="button"
                onClick={() => setShowSettingsModal(false)}
                className="btn-secondary flex-1"
                disabled={isSavingToken}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveBearerToken}
                disabled={!bearerToken.trim() || isSavingToken}
                className="btn-primary flex-1"
              >
                {isSavingToken ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  'Save Token'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* BulletProof Domain Management Modal */}
      {showBPManageModal && selectedBPDomain && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-card-bg border border-border-default rounded-xl p-6 w-full max-w-3xl mx-4 max-h-[85vh] overflow-y-auto animate-scale-in shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-accent-purple/10 rounded-lg flex items-center justify-center">
                  <Globe className="w-5 h-5 text-accent-purple" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-text-primary">{selectedBPDomain.domain}</h3>
                  <p className="text-sm text-text-muted">BulletProof Domain Management</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowBPManageModal(false);
                  setSelectedBPDomain(null);
                  setBpDomainStatus(null);
                }}
                className="p-2 hover:bg-table-row-hover rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-text-muted" />
              </button>
            </div>

            {isLoadingBPStatus ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-accent-blue animate-spin" />
              </div>
            ) : (
              <div className="space-y-6">
                {/* Current Status */}
                {bpDomainStatus && (
                  <div className="p-4 bg-table-bg border border-border-default rounded-lg">
                    <h4 className="font-semibold text-text-primary mb-3 flex items-center space-x-2">
                      <Eye className="w-4 h-4" />
                      <span>Current Status</span>
                    </h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-text-muted">Bind Type:</span>
                        <span className="ml-2 text-text-primary font-medium">{bpDomainStatus.bind_type}</span>
                      </div>
                      <div>
                        <span className="text-text-muted">Category:</span>
                        <span className="ml-2 text-text-primary font-medium">{bpDomainStatus.category}</span>
                      </div>
                      {bpDomainStatus.ip && (
                        <div>
                          <span className="text-text-muted">Current IP:</span>
                          <span className="ml-2 text-text-primary font-medium">{bpDomainStatus.ip}</span>
                        </div>
                      )}
                      <div>
                        <span className="text-text-muted">SSL Mode:</span>
                        <span className="ml-2 text-text-primary font-medium">{bpDomainStatus.ssl_mode}</span>
                      </div>
                      <div>
                        <span className="text-text-muted">Security:</span>
                        <span className="ml-2 text-text-primary font-medium">{bpDomainStatus.security_mode}</span>
                      </div>
                    </div>

                    {/* Ban Status */}
                    {(bpDomainStatus.registrar_ban || bpDomainStatus.google_ban || bpDomainStatus.metamask_ban) && (
                      <div className="mt-3 pt-3 border-t border-border-default">
                        <div className="flex items-center space-x-2 text-accent-red">
                          <AlertTriangle className="w-4 h-4" />
                          <span className="text-sm font-medium">Ban Status:</span>
                        </div>
                        <div className="mt-2 space-y-1 text-xs">
                          {bpDomainStatus.registrar_ban && <div className="text-accent-red">• Registrar Ban</div>}
                          {bpDomainStatus.google_ban && <div className="text-accent-red">• Google Ban</div>}
                          {bpDomainStatus.metamask_ban && <div className="text-accent-red">• MetaMask Ban</div>}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Change IP */}
                  <div className="card p-4">
                    <h4 className="font-semibold text-text-primary mb-3 flex items-center space-x-2">
                      <Network className="w-4 h-4 text-accent-blue" />
                      <span>Change IP Address</span>
                    </h4>
                    <div className="space-y-3">
                      <input
                        type="text"
                        className="w-full px-3 py-2 bg-table-bg border border-border-default rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent-blue text-sm"
                        value={bpNewIP}
                        onChange={(e) => setBpNewIP(e.target.value)}
                        placeholder="192.168.1.1"
                      />
                      <button
                        onClick={handleBPChangeIP}
                        disabled={!bpNewIP.trim() || isBPOperating}
                        className="btn-primary w-full flex items-center justify-center space-x-2 disabled:opacity-50 text-sm py-2"
                      >
                        {isBPOperating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Network className="w-4 h-4" />}
                        <span>Update IP</span>
                      </button>
                    </div>
                  </div>

                  {/* SSL Mode */}
                  <div className="card p-4">
                    <h4 className="font-semibold text-text-primary mb-3 flex items-center space-x-2">
                      <Shield className="w-4 h-4 text-accent-green" />
                      <span>SSL/TLS Mode</span>
                    </h4>
                    <div className="space-y-3">
                      <div className="grid grid-cols-3 gap-2">
                        {(['off', 'flexible', 'full'] as const).map((mode) => (
                          <button
                            key={mode}
                            onClick={() => setBpSSLMode(mode)}
                            className={`p-2 border-2 rounded-lg transition-all text-xs font-medium capitalize ${bpSSLMode === mode
                              ? 'border-accent-green bg-accent-green/10 text-accent-green'
                              : 'border-border-default text-text-muted hover:border-accent-green/50'
                              }`}
                          >
                            {mode}
                          </button>
                        ))}
                      </div>
                      <button
                        onClick={handleBPChangeSSL}
                        disabled={isBPOperating}
                        className="btn-primary w-full flex items-center justify-center space-x-2 disabled:opacity-50 text-sm py-2"
                      >
                        {isBPOperating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                        <span>Update SSL</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Security Mode */}
                <div className="card p-4">
                  <h4 className="font-semibold text-text-primary mb-3 flex items-center space-x-2">
                    <Lock className="w-4 h-4 text-accent-purple" />
                    <span>Security Level</span>
                  </h4>
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-2">
                      {(['essentially_off', 'low', 'medium', 'high', 'under_attack'] as const).map((mode) => (
                        <button
                          key={mode}
                          onClick={() => setBpSecurityMode(mode)}
                          className={`p-2 border-2 rounded-lg transition-all text-xs font-medium capitalize ${bpSecurityMode === mode
                            ? 'border-accent-purple bg-accent-purple/10 text-accent-purple'
                            : 'border-border-default text-text-muted hover:border-accent-purple/50'
                            }`}
                        >
                          {mode.replace('_', ' ')}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={handleBPChangeSecurity}
                      disabled={isBPOperating}
                      className="btn-primary w-full flex items-center justify-center space-x-2 disabled:opacity-50 text-sm py-2"
                    >
                      {isBPOperating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                      <span>Update Security</span>
                    </button>
                  </div>
                </div>

                {/* Subdomains Management */}
                <div className="card p-4">
                  <h4 className="font-semibold text-text-primary mb-3 flex items-center space-x-2">
                    <Globe className="w-4 h-4 text-accent-blue" />
                    <span>Subdomains</span>
                  </h4>

                  {/* Add Subdomain */}
                  <div className="space-y-3 mb-4">
                    <input
                      type="text"
                      className="w-full px-3 py-2 bg-table-bg border border-border-default rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent-blue text-sm"
                      value={newSubdomain}
                      onChange={(e) => setNewSubdomain(e.target.value)}
                      placeholder={`api.${selectedBPDomain?.domain || 'example.com'}`}
                    />
                    <input
                      type="text"
                      className="w-full px-3 py-2 bg-table-bg border border-border-default rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent-blue text-sm"
                      value={subdomainIP}
                      onChange={(e) => setSubdomainIP(e.target.value)}
                      placeholder="192.0.2.1"
                    />
                    <button
                      onClick={handleAddSubdomain}
                      disabled={!newSubdomain.trim() || !subdomainIP.trim() || isAddingSubdomain}
                      className="btn-primary w-full flex items-center justify-center space-x-2 disabled:opacity-50 text-sm py-2"
                    >
                      {isAddingSubdomain ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                      <span>Add Subdomain</span>
                    </button>
                  </div>

                  {/* Subdomains List */}
                  {isLoadingSubdomains ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="w-5 h-5 text-accent-blue animate-spin" />
                    </div>
                  ) : subdomains.length > 0 ? (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {subdomains.map((record) => {
                        const mainDomain = selectedBPDomain?.domain || '';
                        const isRootDomain = record.name === mainDomain || record.name === '@';
                        const isSubdomain = !isRootDomain;

                        // If name already contains full domain, use as is
                        let displayName = record.name;
                        if (!displayName.includes('.') && !isRootDomain) {
                          // Only prefix, add main domain
                          displayName = `${record.name}.${mainDomain}`;
                        } else if (isRootDomain) {
                          displayName = mainDomain;
                        }

                        return (
                          <div key={record.id} className="flex items-center justify-between p-2 bg-table-bg rounded-lg">
                            <div className="flex-1">
                              <div className="text-sm font-medium text-text-primary">{displayName}</div>
                              <div className="text-xs text-text-muted">{record.type}: {record.content}</div>
                            </div>
                            {isSubdomain && (
                              <button
                                onClick={() => handleDeleteSubdomain(record.id)}
                                className="p-1.5 text-accent-red hover:bg-accent-red/10 rounded transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-text-muted text-center py-4">No subdomains configured</p>
                  )}
                </div>

                {/* Refresh Status */}
                <button
                  onClick={async () => {
                    await loadBPDomainStatus(selectedBPDomain.id);
                    await loadSubdomains(selectedBPDomain.id);
                  }}
                  disabled={isLoadingBPStatus}
                  className="btn-secondary w-full flex items-center justify-center space-x-2"
                >
                  <RefreshCw className={`w-4 h-4 ${isLoadingBPStatus ? 'animate-spin' : ''}`} />
                  <span>Refresh Status</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CloudAPI Settings Modal */}
      {showCFModal && selectedCFDomain && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card-bg border border-border-default rounded-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-text-primary">
                CloudAPI Settings
              </h3>
              <button
                onClick={() => setShowCFModal(false)}
                className="p-2 hover:bg-table-row-hover rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-text-muted" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="p-3 bg-accent-blue/10 border border-accent-blue/20 rounded-lg">
                <p className="text-accent-blue text-sm font-medium mb-1">
                  Domain: {selectedCFDomain.domain}
                </p>
                <p className="text-accent-blue text-xs">
                  Configure CloudAPI credentials for this domain
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-text-primary mb-2">
                  CloudAPI Type
                </label>
                <select
                  className="w-full px-4 py-3 bg-table-bg border border-border-default rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent transition-all"
                  value={cloudApiType}
                  onChange={(e) => setCloudApiType(e.target.value as 'cloudflare' | 'bulletproof' | '')}
                >
                  <option value="">Select API Type</option>
                  <option value="cloudflare">Cloudflare</option>
                  <option value="bulletproof">BulletProof</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-text-primary mb-2">
                  Email
                </label>
                <input
                  type="email"
                  className="w-full px-4 py-3 bg-table-bg border border-border-default rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent transition-all"
                  value={cfDomainEmail}
                  onChange={(e) => setCfDomainEmail(e.target.value)}
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-text-primary mb-2">
                  API Key/Token
                </label>
                <input
                  type="password"
                  className="w-full px-4 py-3 bg-table-bg border border-border-default rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent transition-all"
                  value={cfDomainToken}
                  onChange={(e) => setCfDomainToken(e.target.value)}
                  placeholder="Enter API key/token"
                />
                <p className="text-xs text-text-muted mt-2">
                  {cloudApiType === 'cloudflare' ? 'Global API Key or API Token with Zone:Edit permissions' : 'BulletProof API Key'}
                </p>
              </div>

              <div className="flex items-center space-x-3 pt-4">
                <button
                  onClick={() => setShowCFModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveCFSettings}
                  disabled={isSavingCF || !cloudApiType || !cfDomainEmail.trim() || !cfDomainToken.trim()}
                  className="btn-primary flex-1 flex items-center justify-center space-x-2"
                >
                  {isSavingCF ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <span>Save Settings</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Analytics Modal */}
      {selectedAnalyticsDomain && (
        <AnalyticsModal
          isOpen={showAnalyticsModal}
          domainName={selectedAnalyticsDomain.domain}
          domainId={selectedAnalyticsDomain.id}
          onClose={() => {
            setShowAnalyticsModal(false);
            setSelectedAnalyticsDomain(null);
          }}
        />
      )}

      {/* Import BP Domains Modal */}
      {/* Import BP Domains Modal */}
      {showImportBPModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card-bg border border-border-default rounded-xl p-6 w-full max-w-6xl h-[90vh] mx-4 overflow-hidden flex flex-col animate-scale-in shadow-2xl">
            <div className="flex items-center justify-between mb-6 shrink-0">
              <div>
                <h3 className="text-xl font-bold text-text-primary">Import BulletProof Domains</h3>
                <p className="text-sm text-text-muted mt-1">Select domains to import from your account</p>
              </div>
              <button
                onClick={() => {
                  setShowImportBPModal(false);
                  setBpAccountDomains([]);
                  setSelectedBPImports(new Set());
                }}
                className="p-2 hover:bg-table-row-hover rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-text-muted" />
              </button>
            </div>

            {/* Proxy Warning Banner */}
            {!isLoadingBPDomains && monitoringStatus && !monitoringStatus.hasProxies && (
              <div className="mb-4 p-4 bg-accent-red/10 border border-accent-red/30 rounded-lg">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-5 h-5 text-accent-red mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-accent-red font-semibold mb-1">No Proxies Configured</p>
                    <p className="text-accent-red text-sm">
                      CT checks may fail or trigger CAPTCHAs without proxies. Add proxies in Settings → Proxies to enable reliable domain checking.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {isLoadingBPDomains ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <Loader2 className="w-10 h-10 animate-spin text-accent-purple mx-auto mb-4" />
                  <p className="text-text-muted">Loading domains from BulletProof...</p>
                </div>
              </div>
            ) : bpAccountDomains.length === 0 ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <Globe className="w-12 h-12 text-text-muted mx-auto mb-4" />
                  <p className="text-text-primary font-medium">No domains found</p>
                  <p className="text-sm text-text-muted">Your BulletProof account has no domains</p>
                </div>
              </div>
            ) : (
              <div className="flex-1 flex flex-col min-h-0">
                <div className="flex items-center justify-between mb-4 shrink-0 px-1">
                  <span className="text-sm text-text-muted">{selectedBPImports.size} domains selected</span>
                  <div className="flex items-center space-x-3">
                    <button
                      type="button"
                      onClick={handleCheckCTStatus}
                      disabled={isCheckingCT}
                      className="px-3 py-1.5 text-xs font-medium bg-accent-blue/10 text-accent-blue rounded-lg hover:bg-accent-blue/20 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Refresh data from BulletProof"
                    >
                      <RefreshCw className={`w-3 h-3 ${isCheckingCT ? 'animate-spin' : ''}`} />
                      {isCheckingCT ? 'Checking...' : 'Check CT Status'}
                    </button>
                    <button
                      type="button"
                      onClick={selectAllBPImports}
                      className="px-3 py-1.5 text-xs font-medium bg-table-row-dark text-text-muted rounded-lg hover:bg-table-row-hover hover:text-text-primary transition-colors"
                    >
                      Select All
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto pr-2 pb-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {bpAccountDomains.map((domain, idx) => {
                      const isImported = domain.alreadyImported;
                      const isSelected = selectedBPImports.has(domain.domain);
                      // Only check CT status for border color, not BP ban flags
                      const isBlocked = domain.ct_result === 'blocked' || domain.ct_status === 'blocked';
                      const isClean = domain.ct_result === 'clean' || domain.ct_status === 'clean';

                      return (
                        <div
                          key={idx}
                          onClick={() => !isImported && toggleBPImportSelection(domain.domain)}
                          className={`
                              flex items-center justify-between p-4 rounded-xl border transition-all cursor-pointer h-full
                              ${isImported
                              ? 'bg-table-row-hover/50 border-border-default opacity-50 cursor-not-allowed'
                              : isSelected
                                ? 'bg-accent-blue/10 border-accent-blue shadow-lg shadow-accent-blue/5'
                                : 'bg-table-bg border-border-default hover:border-text-muted hover:bg-table-row-hover'
                            }
                              ${isBlocked ? 'border-l-4 border-l-accent-red' :
                              isClean ? 'border-l-4 border-l-accent-green' : ''}
                            `}
                        >
                          <div className="flex items-center space-x-3 min-w-0 flex-1">
                            <div className={`
                                w-5 h-5 rounded border flex items-center justify-center transition-colors shrink-0
                                ${isImported
                                ? 'bg-border-default border-border-default'
                                : isSelected
                                  ? 'bg-accent-blue border-accent-blue'
                                  : 'border-text-muted'
                              }
                              `}>
                              {(isSelected || isImported) && <CheckCircle className="w-3.5 h-3.5 text-white" />}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className="font-medium text-text-primary truncate block" title={domain.domain}>
                                  {domain.domain}
                                </span>
                                {domain.checking && <Loader2 className="w-3 h-3 animate-spin text-accent-blue" />}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-text-muted truncate">
                                  {isImported ? 'Already imported' : `Expire: ${domain.expires_at ? new Date(domain.expires_at).toLocaleDateString() : 'Unknown'}`}
                                </span>
                                {isBlocked ? (
                                  <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-accent-red/20 text-accent-red border border-accent-red/30">
                                    BLOCKED
                                  </span>
                                ) : isClean ? (
                                  <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-accent-green/20 text-accent-green border border-accent-green/30">
                                    CLEAN
                                  </span>
                                ) : null}
                              </div>
                              {domain.ct_details && (
                                <p className="text-[10px] text-accent-red mt-1 truncate" title={domain.ct_details}>
                                  {domain.ct_details}
                                </p>
                              )}
                            </div>
                          </div>
                          {domain.price && (
                            <span className="text-sm font-semibold text-text-secondary ml-3 bg-table-row-dark px-2 py-1 rounded">
                              ${domain.price}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Server Selection */}
                <div className="space-y-2 py-4 border-t border-border-default shrink-0">
                  <label className="block text-sm font-semibold text-text-primary">
                    Link to Server (Optional)
                  </label>
                  <select
                    className="w-full px-4 py-3 bg-table-bg border border-border-default rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent transition-all"
                    value={selectedServerId || ''}
                    onChange={(e) => setSelectedServerId(e.target.value ? parseInt(e.target.value) : null)}
                  >
                    <option value="">No server (add later)</option>
                    {servers.map((server) => (
                      <option key={server.id} value={server.id}>
                        {server.name} ({server.host})
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-text-muted">
                    Select a server to automatically link imported domains
                  </p>
                </div>

                {/* Subdomain Prefix Toggle */}
                <div className="space-y-3 py-4 border-t border-border-default shrink-0">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-semibold text-text-primary">
                      Subdomain Prefix
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <span className={`text-sm ${useStandardPrefix ? 'text-accent-green' : 'text-text-muted'}`}>
                        Use s.
                      </span>
                      <div
                        className={`relative w-12 h-6 rounded-full transition-colors ${useStandardPrefix ? 'bg-accent-green' : 'bg-table-row-dark'}`}
                        onClick={() => setUseStandardPrefix(!useStandardPrefix)}
                      >
                        <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${useStandardPrefix ? 'left-7' : 'left-1'}`} />
                      </div>
                    </label>
                  </div>

                  {!useStandardPrefix && (
                    <div>
                      <input
                        type="text"
                        className="w-full px-4 py-3 bg-table-bg border border-border-default rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent transition-all"
                        value={customPrefix}
                        onChange={(e) => setCustomPrefix(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                        placeholder="custom"
                      />
                      <p className="text-xs text-text-muted mt-1">
                        Imported domains will use: <span className="text-accent-blue">{customPrefix || 'custom'}.domain.com</span>
                      </p>
                    </div>
                  )}

                  {useStandardPrefix && (
                    <p className="text-xs text-text-muted">
                      Imported domains will use: <span className="text-accent-green">s.domain.com</span>
                    </p>
                  )}
                </div>

                <div className="flex justify-end pt-6 space-x-3 border-t border-border-default mt-auto shrink-0">
                  <button
                    onClick={() => setShowImportBPModal(false)}
                    className="btn-secondary"
                    disabled={isImportingBP}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleImportBPDomains}
                    disabled={selectedBPImports.size === 0 || isImportingBP}
                    className="btn-primary"
                  >
                    {isImportingBP ? 'Importing...' : `Import ${selectedBPImports.size} Domains`}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      {
        showUpgradeModal && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowUpgradeModal(false)}>
            <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-8 max-w-sm w-full mx-4 text-center" onClick={(e) => e.stopPropagation()}>
              <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-white/5 flex items-center justify-center">
                <Lock className="w-8 h-8 text-white/40" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Domain Limit Reached</h3>
              <p className="text-white/50 text-sm mb-6">
                You've reached your limit of {domainLimit} domains. Upgrade to add more.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowUpgradeModal(false)}
                  className="flex-1 py-2.5 text-white/60 hover:text-white border border-white/10 rounded-lg hover:border-white/20 transition-colors text-sm"
                >
                  Cancel
                </button>
                <a
                  href="https://ikcore.cloud/pricing"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 py-2.5 bg-white text-black rounded-lg text-sm font-medium hover:bg-white/90 transition-colors flex items-center justify-center gap-2"
                >
                  <Crown className="w-4 h-4" />
                  Upgrade
                  <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default DomainsPanel;